import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

interface VideoCardProps {
  title: string;
  excerpt: string;
  slug: string;
  thumbnail: string;
  category: string;
  categoryName: string;
  views: number;
  date: string;
  featured?: boolean;
}

export default function VideoCard({ title, excerpt, slug, thumbnail, category, categoryName, views, date, featured }: VideoCardProps) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <article
      ref={ref}
      className={`group bg-background-50 rounded-lg border border-background-200 overflow-hidden transition-all duration-500 hover:border-primary-200 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      } ${featured ? 'lg:col-span-2 lg:flex' : ''}`}
    >
      <Link to={`/article/${slug}`} className={`block ${featured ? 'lg:w-3/5' : ''}`}>
        <div className="relative overflow-hidden aspect-video">
          <img
            src={thumbnail}
            alt={title}
            title={`${title} - 红桃视频在线观看`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
          <div className="absolute top-3 left-3">
            <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-primary-500 text-background-50">
              {categoryName}
            </span>
          </div>
          <div className="absolute bottom-3 right-3 flex items-center gap-1 text-xs text-white/80">
            <i className="ri-eye-line"></i>
            <span>{views.toLocaleString()} 次播放</span>
          </div>
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="w-14 h-14 rounded-full bg-primary-500/90 flex items-center justify-center backdrop-blur-sm">
              <i className="ri-play-fill text-2xl text-background-50"></i>
            </div>
          </div>
        </div>
      </Link>
      <div className={`p-4 md:p-5 ${featured ? 'lg:w-2/5 lg:flex lg:flex-col lg:justify-center' : ''}`}>
        <Link to={`/article/${slug}`} className="no-underline">
          <h3 className={`font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 ${featured ? 'text-lg md:text-xl' : 'text-base'}`}>
            {title}
          </h3>
        </Link>
        <p className="mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed">
          {excerpt}
        </p>
        <div className="mt-3 flex items-center gap-3 text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {date}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {views.toLocaleString()} 次播放
          </span>
        </div>
      </div>
    </article>
  );
}